import apiClient from './api';

export type FeedbackVoteType = 'Positive' | 'Negative';
export type FeedbackTriageStatus = 'New' | 'Reviewed' | 'Actioned' | 'Ignored';

export interface ResponseFeedbackListItem {
  id: string;
  assistantMessageId: string;
  userId: string;
  chatSessionId: string;
  widgetId?: string;
  responseId?: string;
  voteType: FeedbackVoteType;
  reasonTag?: string;
  reasonText?: string;
  severity?: 'Low' | 'Medium' | 'High';
  confidence?: 'Low' | 'Medium' | 'High';
  triageStatus: FeedbackTriageStatus;
  triagedBy?: string;
  triagedAt?: string;
  internalNotes?: string;
  submittedAt: string;
  assistantMessagePreview?: string;
}

export interface ResponseFeedbackDetail extends ResponseFeedbackListItem {
  assistantMessageContent?: string;
  aiResponsePayload?: string;
  aiExecutionIsSuccess?: boolean;
  aiExecutionHttpStatusCode?: number;
  aiExecutionAt?: string;
  aiProvider?: string;
  aiModel?: string;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface FeedbackDailyTrendPoint {
  date: string;
  positiveCount: number;
  negativeCount: number;
}

export interface FeedbackNegativeReasonPoint {
  reasonTag: string;
  count: number;
}

const normalizeVoteType = (value: unknown): FeedbackVoteType => {
  if (value === 'Positive' || value === 1) return 'Positive';
  if (value === 'Negative' || value === 2) return 'Negative';
  return 'Negative';
};

const normalizeTriageStatus = (value: unknown): FeedbackTriageStatus => {
  if (value === 'New' || value === 1) return 'New';
  if (value === 'Reviewed' || value === 2) return 'Reviewed';
  if (value === 'Actioned' || value === 3) return 'Actioned';
  if (value === 'Ignored' || value === 4) return 'Ignored';
  return 'New';
};

const normalizeFeedbackItem = <T extends ResponseFeedbackListItem>(item: T): T => {
  return {
    ...item,
    voteType: normalizeVoteType(item.voteType as unknown),
    triageStatus: normalizeTriageStatus(item.triageStatus as unknown),
  };
};

export const feedbackService = {
  getFeedback: async (params: {
    page?: number;
    pageSize?: number;
    vote?: FeedbackVoteType | 'all';
    triageStatus?: FeedbackTriageStatus | 'all';
    reasonTag?: string;
    days?: number;
  }): Promise<PaginatedResult<ResponseFeedbackListItem>> => {
    const query = new URLSearchParams();
    query.set('page', String(params.page ?? 1));
    query.set('pageSize', String(params.pageSize ?? 20));
    query.set('days', String(params.days ?? 7));

    if (params.vote && params.vote !== 'all') query.set('vote', params.vote);
    if (params.triageStatus && params.triageStatus !== 'all') query.set('triageStatus', params.triageStatus);
    if (params.reasonTag) query.set('reasonTag', params.reasonTag);

    const response = await apiClient.get<PaginatedResult<ResponseFeedbackListItem>>(`/feedback?${query.toString()}`);
    return {
      ...response.data,
      items: response.data.items.map(normalizeFeedbackItem),
    };
  },

  getFeedbackById: async (id: string): Promise<ResponseFeedbackDetail> => {
    const response = await apiClient.get<ResponseFeedbackDetail>(`/feedback/${id}`);
    return normalizeFeedbackItem(response.data);
  },

  updateTriageStatus: async (id: string, triageStatus: FeedbackTriageStatus, internalNotes?: string) => {
    const response = await apiClient.patch<ResponseFeedbackDetail>(`/feedback/${id}/triage`, {
      triageStatus,
      internalNotes,
    });
    return normalizeFeedbackItem(response.data);
  },

  getFeedbackDailyTrend: async (days = 7): Promise<FeedbackDailyTrendPoint[]> => {
    const response = await apiClient.get<FeedbackDailyTrendPoint[]>(`/dashboard/feedback-daily-trend?days=${days}`);
    return response.data;
  },

  getNegativeFeedbackByReason: async (days = 7): Promise<FeedbackNegativeReasonPoint[]> => {
    const response = await apiClient.get<FeedbackNegativeReasonPoint[]>(`/dashboard/feedback-negative-by-reason?days=${days}`);
    return response.data;
  },
};